import React, { useEffect, useState } from 'react';
import { Alert, Form, Spinner } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { Instructor, searchInstructors } from '../../_services/schedules.service';

const TeacherPicker: React.FC = () => {
  const history = useHistory();
  const [search, setSearch] = useState<string>('');
  const [results, setResults] = useState<Instructor[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const res = await searchInstructors(term);
        if (cancelled) return;
        setResults(res?.data ?? []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to search instructors');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  const goToTeacher = (id: number) => {
    history.push(`/teacher-schedule/${id}`);
  };

  return (
    <div style={{ padding: '20px 24px', maxWidth: 720 }}>
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border-default)' }}>
        <h4 style={{ margin: 0, fontWeight: 600 }}>Teacher Schedule & Substitution</h4>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
          Pick the teacher who is absent. You'll see their upcoming classes and
          can assign a substitute for one or more dates.
        </div>
      </div>

      <Form.Group>
        <Form.Label>Search Teacher</Form.Label>
        <Form.Control
          type="text"
          autoFocus
          placeholder="Type at least 2 characters of the teacher's name or code"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Form.Group>

      {error && (
        <Alert variant="danger" className="mt-3" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <div
        style={{
          marginTop: 16,
          border: '1px solid var(--border-default)',
          borderRadius: 4,
          minHeight: 200,
          maxHeight: 480,
          overflowY: 'auto',
          background: 'white',
        }}
      >
        {searching && (
          <div style={{ padding: 16, textAlign: 'center' }}>
            <Spinner animation="border" size="sm" /> Searching…
          </div>
        )}
        {!searching && search.trim().length < 2 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
            Start typing to find a teacher.
          </div>
        )}
        {!searching && search.trim().length >= 2 && results.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
            No teachers match.
          </div>
        )}
        {!searching &&
          results.map((i) => (
            <button
              key={i.id}
              type="button"
              onClick={() => goToTeacher(i.id)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                border: 'none',
                borderBottom: '1px solid #f1f3f5',
                background: 'white',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-page)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'white';
              }}
            >
              <div style={{ fontWeight: 500 }}>{i.name || '(no name)'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {i.code ? `${i.code} · ` : ''}
                {i.email || ''}
                {i.phoneNumber ? ` · ${i.phoneNumber}` : ''}
              </div>
            </button>
          ))}
      </div>
    </div>
  );
};

export default TeacherPicker;
